/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import {
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import Link from "../../../ui/link";
import React, { useContext, useEffect, useState } from "react";
import { SidebarContext } from "../context/sidebar.context";
import PropTypes from "prop-types";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { getActiveRoute } from "../../../../helpers/url-helper";
import { useLocation } from "react-router-dom";

const ACCENT = "#3B82F6";
/** Default icon color when not active/hover – soft slate instead of flat gray */
const ICON_DEFAULT_DARK = "#94a3b8";
const ICON_DEFAULT_LIGHT = "#64748b";

/* ─── Sidebar Item ─── */
const SidebarItem = ({ link, open = false, onClick = null }) => {
  const theme = useTheme();
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sidebar = useContext(SidebarContext);
  const Icon = link.Icon || (() => null);
  const location = useLocation();

  const isDark = theme.palette.mode === "dark";
  const textColor = isActive ? ACCENT : isDark ? "#ffffff" : theme.palette.text.primary;
  const iconColorVal = isActive ? ACCENT : isDark ? ICON_DEFAULT_DARK : ICON_DEFAULT_LIGHT;

  useEffect(() => {
    setIsActive(getActiveRoute() === link.link);
  }, [link, location]);

  const buttonSx = {
    margin: "2px 8px",
    minHeight: 36,
    justifyContent: sidebar.opened ? "initial" : "center",
    alignItems: "center",
    px: 1.2,
    position: "relative",
    borderRadius: "8px",
    borderLeft: isActive ? `3px solid ${ACCENT}` : "3px solid transparent",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&:active": {
      backgroundColor: "transparent",
    },
  };

  const crownHovered = link.premiumTooltip && isHovered;
  const CrownIcon = () => (
    <motion.span
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginLeft: "3px",
        position: "relative",
        top: "-2px",
        color: "#E6B800",
      }}
      animate={{
        rotate: crownHovered ? [0, -6, 6, 0] : 0,
        scale: crownHovered ? 1.12 : 1,
        y: crownHovered ? [0, -2, 0] : 0,
      }}
      transition={{
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" width={15} height={15}>
        <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 2c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2v-1h14v1z" />
      </svg>
    </motion.span>
  );

  const iconColor = isHovered && !isActive ? ACCENT : iconColorVal;
  const renderContent = () => (
    <>
      <ListItemIcon sx={{ minWidth: 0, mr: sidebar.opened ? 1 : "auto", justifyContent: "center", overflow: "visible" }}>
        <motion.span
          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          animate={{
            scale: isHovered ? 1.1 : 1,
            y: isHovered ? -2 : 0,
          }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25,
          }}
        >
          <Icon
            sx={{
              color: iconColor,
              fontSize: 22,
              transition: "color 0.2s ease",
            }}
          />
        </motion.span>
      </ListItemIcon>
      <ListItemText
        primary={
          <>
            {link.label}
            {link.premiumTooltip && sidebar.opened && (
              <Tooltip title={link.premiumTooltip} placement="top" arrow>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <CrownIcon />
                </span>
              </Tooltip>
            )}
          </>
        }
        sx={{
          opacity: sidebar.opened ? 1 : 0,
          color: textColor,
          "& .MuiListItemText-primary": {
            fontSize: "0.8rem",
            fontWeight: isActive ? 500 : 400,
            paddingRight: link.count ? "28px" : "0",
            display: "flex",
            alignItems: "center",
          },
        }}
      />
      {!!link.count && sidebar.opened && (
        <Chip
          label={link.count}
          size="small"
          sx={{
            position: "absolute",
            right: "8px",
            top: "50%",
            marginTop: "-9px",
            height: "18px",
            minWidth: "18px",
            fontSize: "0.7rem",
            fontWeight: "500px",
            backgroundColor: ACCENT,
            color: "#fff",
            "& .MuiChip-label": {
              padding: "0 5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        />
      )}
      {sidebar.opened && onClick && (
        <IconButton
          size="small"
          onClick={(e) => { onClick(); e.preventDefault(); }}
          sx={{ padding: "4px", "& svg": { fontSize: "1rem" } }}
        >
          {open ? <ExpandLess /> : onClick && <ExpandMore />}
        </IconButton>
      )}
    </>
  );

  return (
    <ListItem disablePadding sx={{ display: "block" }}>
      {link.isButton ? (
        <Tooltip title={!sidebar.opened ? link.label : ""} placement="right">
          <Box
            component="span"
            sx={{ display: "block" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ListItemButton disableRipple sx={buttonSx} onClick={link.onClick}>
              {renderContent()}
            </ListItemButton>
          </Box>
        </Tooltip>
      ) : link.external ? (
        <Tooltip title={!sidebar.opened ? link.label : ""} placement="right">
          <Box
            component="span"
            sx={{ display: "block" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <a href={link.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "block" }}>
              <ListItemButton disableRipple sx={buttonSx}>
                {renderContent()}
              </ListItemButton>
            </a>
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title={!sidebar.opened ? link.label : ""} placement="right">
          <Box
            component="span"
            sx={{ display: "block" }}
            onMouseEnter={() => { link.preload?.(); setIsHovered(true); }}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* @ts-expect-error custom Link accepts children */}
            <Link to={link.link} style={{ textDecoration: "none", display: "block" }}>
              <ListItemButton disableRipple sx={buttonSx}>
                {renderContent()}
              </ListItemButton>
            </Link>
          </Box>
        </Tooltip>
      )}
    </ListItem>
  );
};

SidebarItem.propTypes = {
  link: PropTypes.object,
  open: PropTypes.bool,
  onClick: PropTypes.func,
};

const SidebarItemWithNested = ({ link }) => {
  const [open, setOpen] = useState(false);
  const sidebar = useContext(SidebarContext);

  useEffect(() => {
    setOpen(false);
  }, [sidebar.opened]);

  return (
    <>
      <SidebarItem link={link} open={open} onClick={() => setOpen(!open)} />
      <Collapse sx={{ pl: 2 }} in={open} timeout="auto" unmountOnExit>
        <SidebarLinks links={link.nestedLinks} />
      </Collapse>
    </>
  );
};

SidebarItemWithNested.propTypes = {
  link: PropTypes.object,
};

const SidebarLinks = ({ links }) => {
  return (
    <List sx={{ py: 0.5 }}>
      {links.map((link, index) => {
        const linkKey = link.link ?? link.label ?? `link-${index}`;
        if (link.nestedLinks) {
          return <SidebarItemWithNested key={linkKey} link={link} />;
        }
        return <SidebarItem key={linkKey} link={link} />;
      })}
    </List>
  );
};

export default SidebarLinks;

SidebarLinks.propTypes = {
  links: PropTypes.array,
};
